import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { StatusBadge } from '@/components/dashboard/StatusBadge';
import {
  Combo,
  comboApi,
  CreateComboRequest,
  UpdateComboRequest,
  EComboStatus,
} from '@/services/combo';
import { streamingServiceApi, StreamingService } from '@/services/streaming-service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Search,
  Plus,
  Eye,
  Pencil,
  Trash2,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Package,
  Clock,
  DollarSign,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/utils';
import { useUrlFilters, useUrlPage, useUrlSearch } from '@/hooks/use-url-filters';

function generatePaginationNumbers(currentPage: number, totalPages: number): (number | string)[] {
  const delta = 1;
  const range: (number | string)[] = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
      range.push(i);
    } else if (range[range.length - 1] !== '...') {
      range.push('...');
    }
  }
  return range;
}

const PAGE_SIZE = 10;

const DEFAULT_FILTERS = {
  status: 'ALL' as EComboStatus | 'ALL',
};

export default function Combos() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery, debouncedSearch] = useUrlSearch('search');
  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS);
  const statusFilter = filters.status;
  const [currentPage, setCurrentPage] = useUrlPage();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingCombo, setEditingCombo] = useState<Combo | null>(null);
  const [deletingCombo, setDeletingCombo] = useState<Combo | null>(null);

  const queryClient = useQueryClient();

  const { data: combosResponse, isLoading, error, isFetching } = useQuery({
    queryKey: ['combos', currentPage, debouncedSearch, statusFilter],
    queryFn: () =>
      comboApi.getAll({
        page: currentPage,
        limit: PAGE_SIZE,
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        search: debouncedSearch || undefined,
      }),
  });

  const { data: servicesResponse } = useQuery({
    queryKey: ['streaming-services-all'],
    queryFn: () => streamingServiceApi.getAll({ page: 1, limit: 100 }),
  });

  const combos = combosResponse?.data || [];
  const pagination = combosResponse?.pagination;
  const totalPages = pagination?.totalPages || 1;
  const totalItems = pagination?.totalItems || 0;
  const availableServices = servicesResponse?.data || [];

  const createMutation = useMutation({
    mutationFn: (data: CreateComboRequest) => comboApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['combos'] });
      setIsCreateDialogOpen(false);
      toast.success('Combo creado exitosamente');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateComboRequest }) =>
      comboApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['combos'] });
      setIsEditDialogOpen(false);
      setEditingCombo(null);
      toast.success('Combo actualizado exitosamente');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => comboApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['combos'] });
      setIsDeleteDialogOpen(false);
      setDeletingCombo(null);
      toast.success('Combo eliminado exitosamente');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleEdit = (combo: Combo) => {
    setEditingCombo(combo);
    setIsEditDialogOpen(true);
  };

  const handleDeleteConfirm = (combo: Combo) => {
    setDeletingCombo(combo);
    setIsDeleteDialogOpen(true);
  };

  return (
    <DashboardLayout title="Combos" subtitle="Administra los combos de servicios de streaming">
      <div className="space-y-6 animate-fade-in">
        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-3 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Buscar combos por nombre..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select
              value={statusFilter}
              onValueChange={(value) => setFilters({ status: value as EComboStatus | 'ALL' })}
            >
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filtrar por estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos los estados</SelectItem>
                <SelectItem value={EComboStatus.ACTIVE}>Activos</SelectItem>
                <SelectItem value={EComboStatus.INACTIVE}>Inactivos</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button className="gap-2" onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="h-4 w-4" />
            Nuevo Combo
          </Button>
        </div>

        {/* Error State */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>
              Error al cargar los combos. Por favor, intenta de nuevo.
            </AlertDescription>
          </Alert>
        )}

        {/* Combos Table */}
        <div className="card-elevated overflow-hidden relative">
          {isFetching && !isLoading && (
            <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-10">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          )}

          {isLoading ? (
            <div className="p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
              <p className="text-muted-foreground mt-2">Cargando combos...</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="font-semibold">Combo</TableHead>
                  <TableHead className="font-semibold text-center">Servicios</TableHead>
                  <TableHead className="font-semibold text-center">Duración</TableHead>
                  <TableHead className="font-semibold text-right">Precio</TableHead>
                  <TableHead className="font-semibold">Estado</TableHead>
                  <TableHead className="font-semibold text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {combos.map((combo) => (
                  <TableRow key={combo.id} className="transition-colors hover:bg-muted/30">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                          <Package className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{combo.name}</p>
                          <p className="text-xs text-muted-foreground">
                            Creado {new Date(combo.createdAt).toLocaleDateString('es-CO')}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {combo.comboServices && combo.comboServices.length > 0 ? (
                        <div className="flex items-center justify-center gap-1 flex-wrap">
                          {combo.comboServices.map((cs) => (
                            <span
                              key={cs.id}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground"
                            >
                              {cs.service?.icon && <span>{cs.service.icon}</span>}
                              {cs.service?.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold text-sm">
                          —
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1 text-muted-foreground">
                        <Clock className="h-3.5 w-3.5" />
                        <span className="text-sm">{combo.duration} días</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-semibold text-foreground">
                      <div className="flex items-center justify-end gap-1">
                        <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
                        {formatCurrency(combo.price)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={combo.status} />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => navigate(`/combos/${combo.id}`)}
                        >
                          <Eye className="h-4 w-4 text-muted-foreground" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleEdit(combo)}
                        >
                          <Pencil className="h-4 w-4 text-muted-foreground" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleDeleteConfirm(combo)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {!isLoading && combos.length === 0 && (
            <div className="p-8 text-center">
              <Package className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
              <p className="text-muted-foreground">No se encontraron combos</p>
              <p className="text-sm text-muted-foreground mt-1">
                Ajusta los filtros o crea un nuevo combo
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4">
            <p className="text-sm text-muted-foreground">
              Mostrando {combos.length} de {totalItems} combos
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1 || isFetching}
              >
                <ChevronLeft className="h-4 w-4" />
                Anterior
              </Button>
              <div className="flex items-center gap-1">
                {generatePaginationNumbers(currentPage, totalPages).map((page, idx) =>
                  page === '...' ? (
                    <span key={`ellipsis-${idx}`} className="px-2 text-muted-foreground">
                      ...
                    </span>
                  ) : (
                    <Button
                      key={page}
                      variant={currentPage === page ? 'default' : 'outline'}
                      size="sm"
                      className="w-9"
                      onClick={() => setCurrentPage(page as number)}
                      disabled={isFetching}
                    >
                      {page}
                    </Button>
                  ),
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages || isFetching}
              >
                Siguiente
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Create Combo Dialog */}
      <CreateComboDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onSubmit={(data) => createMutation.mutate(data)}
        isLoading={createMutation.isPending}
        availableServices={availableServices}
      />

      {/* Edit Combo Dialog */}
      {editingCombo && (
        <EditComboDialog
          open={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          combo={editingCombo}
          onSubmit={(data) => updateMutation.mutate({ id: editingCombo.id, data })}
          isLoading={updateMutation.isPending}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar combo?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. El combo{' '}
              <span className="font-semibold text-foreground">"{deletingCombo?.name}"</span> será
              eliminado permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingCombo && deleteMutation.mutate(deletingCombo.id)}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Eliminando...
                </>
              ) : (
                'Eliminar'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}

// ─── Create Dialog ────────────────────────────────────────────────────────────

function CreateComboDialog({
  open,
  onOpenChange,
  onSubmit,
  isLoading,
  availableServices,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CreateComboRequest) => void;
  isLoading: boolean;
  availableServices: StreamingService[];
}) {
  const [formData, setFormData] = useState<Omit<CreateComboRequest, 'services'>>({
    name: '',
    price: 0,
    duration: 30,
    notes: '',
  });
  const [selectedServices, setSelectedServices] = useState<number[]>([]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedServices.length === 0) {
      return;
    }
    onSubmit({ ...formData, services: selectedServices.map(Number) });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'number' ? Number(value) : value,
    });
  };

  const toggleService = (id: number) => {
    const numId = Number(id);
    setSelectedServices((prev) =>
      prev.includes(numId) ? prev.filter((s) => s !== numId) : [...prev, numId],
    );
  };

  useEffect(() => {
    if (!open) {
      setFormData({ name: '', price: 0, duration: 30, notes: '' });
      setSelectedServices([]);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nuevo Combo</DialogTitle>
          <DialogDescription>Crea un nuevo combo de servicios de streaming</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nombre *</Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Ej: Combo Familiar"
              required
              disabled={isLoading}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="price">Precio *</Label>
              <Input
                id="price"
                name="price"
                type="number"
                min={0}
                value={formData.price}
                onChange={handleChange}
                placeholder="35000"
                required
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="duration">Duración (días) *</Label>
              <Input
                id="duration"
                name="duration"
                type="number"
                min={1}
                value={formData.duration}
                onChange={handleChange}
                placeholder="30"
                required
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>
              Servicios incluidos *{' '}
              <span className="text-muted-foreground font-normal text-xs">
                (selecciona al menos uno)
              </span>
            </Label>
            {availableServices.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No hay servicios disponibles. Crea un servicio primero.
              </p>
            ) : (
              <div className="border rounded-md divide-y max-h-48 overflow-y-auto">
                {availableServices.map((service) => {
                  const checked = selectedServices.includes(Number(service.id));
                  return (
                    <label
                      key={service.id}
                      className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-muted/40 transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleService(service.id)}
                        disabled={isLoading}
                        className="accent-primary"
                      />
                      <span className="text-lg leading-none">{service.icon}</span>
                      <span className="text-sm font-medium">{service.name}</span>
                    </label>
                  );
                })}
              </div>
            )}
            {selectedServices.length === 0 && (
              <p className="text-xs text-destructive">Debes seleccionar al menos un servicio.</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notas</Label>
            <Textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Notas adicionales sobre el combo..."
              disabled={isLoading}
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading || selectedServices.length === 0}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creando...
                </>
              ) : (
                'Crear Combo'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Edit Dialog ──────────────────────────────────────────────────────────────

function EditComboDialog({
  open,
  onOpenChange,
  combo,
  onSubmit,
  isLoading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  combo: Combo;
  onSubmit: (data: UpdateComboRequest) => void;
  isLoading: boolean;
}) {
  const [formData, setFormData] = useState<UpdateComboRequest>({
    name: combo.name,
    price: combo.price,
    duration: combo.duration,
    status: combo.status,
  });

  useEffect(() => {
    setFormData({
      name: combo.name,
      price: combo.price,
      duration: combo.duration,
      status: combo.status,
    });
  }, [combo]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'number' ? Number(value) : value,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editar Combo</DialogTitle>
          <DialogDescription>Actualiza la información del combo</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-name">Nombre</Label>
            <Input
              id="edit-name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Ej: Combo Familiar"
              disabled={isLoading}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-price">Precio</Label>
              <Input
                id="edit-price"
                name="price"
                type="number"
                min={0}
                value={formData.price}
                onChange={handleChange}
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-duration">Duración (días)</Label>
              <Input
                id="edit-duration"
                name="duration"
                type="number"
                min={1}
                value={formData.duration}
                onChange={handleChange}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-status">Estado</Label>
            <select
              id="edit-status"
              name="status"
              value={formData.status}
              onChange={(e) =>
                setFormData({ ...formData, status: e.target.value as EComboStatus })
              }
              disabled={isLoading}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value={EComboStatus.ACTIVE}>Activo</option>
              <option value={EComboStatus.INACTIVE}>Inactivo</option>
            </select>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                'Guardar cambios'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
